import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '../lib/supabase';

interface UseAuthRedirectOptions {
  redirectTo: string;
  redirectIfAuthenticated?: boolean; // If true, redirect when authenticated (opposite behavior)
  waitForSession?: boolean; // Wait for session to initialize before redirecting (default: true)
  delay?: number; // Delay in ms to wait for session restoration (default: 150)
}

/**
 * Hook to handle authentication-based redirects with proper session initialization handling.
 * Prevents flash of login page for authenticated users by waiting for session to restore.
 * 
 * @param options - Configuration options for the redirect
 * @returns Object with `isChecking` (true while checking session) and `shouldRender` (true if page should render)
 */
export function useAuthRedirect(options: UseAuthRedirectOptions) {
  const { redirectTo, redirectIfAuthenticated = false, waitForSession = true, delay = 150 } = options;
  const session = useSession();
  const navigate = useNavigate();
  const [hasCheckedSession, setHasCheckedSession] = useState(false);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }

    const shouldRedirect = redirectIfAuthenticated ? !!session : !session;

    // If we have the expected session state, mark as checked
    if (shouldRedirect === false) {
      setHasCheckedSession(true);
      return;
    }

    // If we've already checked and should redirect, redirect immediately
    if (hasCheckedSession) {
      navigate(redirectTo);
      return;
    }

    // If we don't want to wait for session, redirect immediately
    if (!waitForSession) {
      setHasCheckedSession(true);
      navigate(redirectTo);
      return;
    }

    // Wait a bit for Supabase to restore session from storage before redirecting
    // This prevents the flash of login page for authenticated users
    redirectTimeoutRef.current = setTimeout(() => {
      // Check session again - it might have been restored by now
      supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
        setHasCheckedSession(true);
        const shouldStillRedirect = redirectIfAuthenticated ? !!currentSession : !currentSession;
        if (shouldStillRedirect) {
          navigate(redirectTo);
        }
      });
    }, delay);

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };
  }, [session, navigate, redirectTo, redirectIfAuthenticated, waitForSession, delay, hasCheckedSession]);

  return {
    isChecking: !hasCheckedSession && waitForSession,
    shouldRender: hasCheckedSession && (redirectIfAuthenticated ? !session : !!session),
  };
}

